/**
 * @fileoverview Hook Form Layer para gestión y validación del formulario de chat en Inbox.
 * @module useChatForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {Object} UseChatFormReturn
 * @property {Object} methods - Métodos de react-hook-form para control y validación
 * @property {number} maxChars - Máximo de caracteres permitidos en el mensaje
 * @property {string} placeholder - Placeholder internacionalizado para el input
 */

/**
 * Hook Form Layer especializado para la gestión y validación del formulario de chat en Inbox.
 *
 * Implementa validación dinámica con Yup, valores por defecto y memoización completa.
 *
 * - Capa: Form Layer (solo estado y validación de formulario)
 * - Validación internacionalizada
 * - Identificadores únicos para mensajes y comentarios
 * - Optimización con useMemo
 *
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.conversation - Objeto de conversación actual
 * @param {string|number} params.conversation.id - ID de la conversación
 * @param {boolean} [params.conversation.isComment] - Si el mensaje es comentario
 *
 * @returns {UseChatFormReturn} Objeto con métodos y configuraciones del formulario
 *
 * @example
 * const { methods, maxChars, placeholder } = useChatForm({ conversation });
 * <FormProviderMC methods={methods}>
 *   <InputComponent name="message" maxLength={maxChars} placeholder={placeholder} />
 * </FormProviderMC>
 *
 * @see {@link FormProviderMC} Componente proveedor de formulario
 * @see {@link InputComponent} Componente de input para mensajes
 */
export default function useChatForm({ maxChars }) {
  const { t } = useTranslation('InboxMC');

  /**
   * Esquema de validación Yup para el mensaje de chat.
   * - Requiere mensaje
   * - Máximo de caracteres
   */
  const schema = useMemo(() =>
    object().shape({
      message: string()
        .required(t('chat.required'))
        .max(maxChars, t('chat.max_chars', { max: maxChars }))
    }),
    [t, maxChars]
  );

  /**
   * Valores por defecto memoizados para evitar re-creación
   */
  const defaultValues = useMemo(() => ({
    message: '',
  }), []);

  /**
   * Métodos de react-hook-form para control y validación del formulario.
   * Usar modo 'onChange' solo si es necesario, por defecto es 'onSubmit' que es más performante
   */
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onSubmit', // Cambiar de 'onChange' a 'onSubmit' para mejor performance
    shouldFocusError: false, // Desactivar auto-focus para evitar re-renders
    shouldUnregister: false, // Mantener valores aunque se desmonte
  });

  /**
   * Retorno memoizado con métodos y configuraciones del formulario.
   */
  return useMemo(() => ({
    methods,
    placeholder: t('chat.placeholder')
  }), [methods, t]);
}
